import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Scrolls to the top on a new navigation and restores the previous offset when
 * the user goes back or forward.
 *
 * Replaces the `react-router-scroll-memory` package, which is built on
 * `withRouter` — an API v7 removed, so the package cannot work at all under v7.
 * The behaviour here is deliberately identical to what it did: remember
 * scrollTop per history entry, scroll to 0 on a push, restore the remembered
 * offset on a pop, and do nothing on first mount so the browser's own handling
 * of a #hash in the entry URL still works.
 */
const ScrollMemory = () => {
  const location = useLocation();
  const navigationType = useNavigationType();

  const positions = useRef(new Map());
  const previousKey = useRef(location.key);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // This effect runs after the incoming page has rendered, but the browser
    // does not reset scroll on a client-side navigation — so the current offset
    // is still the one belonging to the page being left.
    positions.current.set(previousKey.current, window.pageYOffset);
    previousKey.current = location.key;

    const remembered = positions.current.get(location.key);
    const target =
      navigationType === "POP" && remembered !== undefined ? remembered : 0;

    window.requestAnimationFrame(() => window.scrollTo(0, target));
  }, [location.key, navigationType]);

  return null;
};

export default ScrollMemory;
